import { messagingApi } from '@/api/MessagingApi'
import type {
	ChatListMessagesResponse,
	ChatMessage,
	LocalAttachment,
} from '@/api/types/MessagingApi'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { CHAT_MESSAGES_QUERY_KEY } from './useChatMessages'

type Snapshot = ChatListMessagesResponse | undefined

/** What the patient is sending: words, files, or both. A retry names the failed message it replaces. */
export type OutgoingMessage = {
	body: string
	attachments: LocalAttachment[]
	retryOf?: ChatMessage
}

/**
 * A message that failed to send, with what it was made of so it can be sent again. Kept apart
 * from the server's list so a poll cannot wipe it, and for the session only.
 */
type FailedMessage = { message: ChatMessage; outgoing: OutgoingMessage }

const useFailedChatMessages = create<{
	failed: FailedMessage[]
	add: (entry: FailedMessage) => void
	remove: (id: number) => void
}>((set) => ({
	failed: [],
	add: (entry) => set((state) => ({ failed: [...state.failed, entry] })),
	remove: (id) =>
		set((state) => ({ failed: state.failed.filter((f) => f.message.id !== id) })),
}))

/** The message as it will look once sent, so the thread can show it straight away. */
const optimisticMessage = (
	outgoing: OutgoingMessage,
	conversationId: number
): ChatMessage => ({
	// A retry keeps its id and place, so the bubble stays put instead of leaving and returning.
	id: outgoing.retryOf?.id ?? -Date.now(),
	conversationId,
	body: outgoing.body || null,
	direction: 'INCOMING',
	createdAt: outgoing.retryOf?.createdAt ?? new Date().toISOString(),
	consumedAt: null,
	attachments: outgoing.attachments.map((file) => ({
		url: file.uri,
		filename: file.filename,
		contentType: file.contentType,
	})),
	pending: true,
})

/**
 * Sends one message, showing it in the thread at once. Files upload first, one by one, then
 * the message goes with their ids. A failed send is pulled from the server list and kept as a
 * failed message the patient can retry or discard.
 */
const useSendChatMessage = () => {
	const queryClient = useQueryClient()
	const { failed, add, remove } = useFailedChatMessages()

	const mutation = useMutation({
		mutationFn: async (outgoing: OutgoingMessage) => {
			const assetIds: number[] = []
			for (const file of outgoing.attachments) {
				const uploaded = await messagingApi.uploadChatAttachment(file)
				assetIds.push(uploaded.assetId)
			}
			return messagingApi.sendChatMessage(outgoing.body, assetIds)
		},
		onMutate: async (outgoing) => {
			const previous = queryClient.getQueryData<Snapshot>(CHAT_MESSAGES_QUERY_KEY)
			const optimistic = optimisticMessage(
				outgoing,
				previous?.messages[0]?.conversationId ?? 0
			)
			// In the list first, then out of the failed set, so a retry never blinks out.
			queryClient.setQueryData<Snapshot>(CHAT_MESSAGES_QUERY_KEY, (old) => ({
				messages: [...(old?.messages ?? []), optimistic],
				unreadCount: old?.unreadCount ?? 0,
			}))
			if (outgoing.retryOf) remove(outgoing.retryOf.id)
			await queryClient.cancelQueries({ queryKey: CHAT_MESSAGES_QUERY_KEY })
			return { previous, optimistic }
		},
		onError: (_error, outgoing, context) => {
			if (!context) return
			// Into the failed set first, then out of the list, for the same reason.
			add({
				message: { ...context.optimistic, pending: false, failed: true },
				outgoing: { ...outgoing, retryOf: undefined },
			})
			queryClient.setQueryData<Snapshot>(CHAT_MESSAGES_QUERY_KEY, (old) =>
				old
					? {
							...old,
							messages: old.messages.filter((m) => m.id !== context.optimistic.id),
					  }
					: old
			)
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: CHAT_MESSAGES_QUERY_KEY })
		},
	})

	const retry = (message: ChatMessage) => {
		const entry = failed.find((f) => f.message.id === message.id)
		if (entry) mutation.mutate({ ...entry.outgoing, retryOf: message })
	}

	return {
		...mutation,
		failed: failed.map((f) => f.message),
		retry,
		discard: remove,
	}
}

export { useSendChatMessage }
